import * as Types from 'src/generated/webapp_gql';
import styled from '@emotion/styled';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    VictoryAxis,
    VictoryChart,
    VictoryGroup,
    VictoryLabel,
    VictoryLine,
    VictoryLegend,
    VictoryScatter,
} from 'victory';
import {
    colors as dsColors,
    defaultFontFamily as defaultFont,
} from '@modernhealth/vital-foundation';
import { BodyTextSmall, ComponentTextSmall } from '@modernhealth/vital-components';
import { WellbeingReportSummaryModal } from './WellbeingReportSummaryModal';
import { WellbeingReportHeader } from './WellbeingReportHeader';

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    margin: '0 auto',
});

const ChartWrapper = styled.div({
    border: `2px solid ${dsColors.gray05}`,
    borderRadius: '6px',
});

const Span = styled.span({
    display: 'inline-block',
    height: '100%',
    cursor: 'pointer',
});

export enum WellbeingReportChartTestID {
    Chart = 'WellbeingReportChart.Chart',
}

interface Props {
    loading?: boolean;
    wellbeingReport?: Array<Types.ReportDataPoint>;
    metadata?: Types.ReportMetadata;
}

/**
 * For more info on Victory chart
 * visit https://formidable.com/open-source/victory/docs
 */

export const WellbeingReportChart: React.FC<Props> = ({ loading, wellbeingReport, metadata }) => {
    const [showModal, setShowModal] = useState(false);
    const { t } = useTranslation();

    return (
        <Wrapper>
            <WellbeingReportHeader title={t('assessments:wellbeing_report.well_being_summary')} />
            <BodyTextSmall marginBottom={24}>
                {t('assessments:wellbeing_report.well_being_summary.subtitle')}{' '}
                <Span onClick={() => setShowModal(true)}>
                    <ComponentTextSmall underline fontColor="blue55">
                        {t('assessments:wellbeing_report.learn_more')}
                    </ComponentTextSmall>
                </Span>
            </BodyTextSmall>
            {loading && <div>Loading...</div>}
            {!loading && (
                <ChartWrapper data-testid={WellbeingReportChartTestID.Chart}>
                    <VictoryChart
                        width={540}
                        height={250}
                        padding={{ top: 72, right: 36, bottom: 60, left: 66 }}
                        domainPadding={{ x: [40, 20] }}
                    >
                        <VictoryLegend
                            x={310}
                            y={14}
                            title={t('assessments:wellbeing_report.well_being_summary.legend')}
                            orientation="horizontal"
                            style={{
                                border: { stroke: 'none' },
                                title: {
                                    fontSize: 12,
                                    fontFamily: defaultFont,
                                    fill: dsColors.gray55,
                                },
                            }}
                            gutter={0}
                            data={[]} // set nothing to display for legend's column data
                        />
                        <VictoryAxis
                            dependentAxis // y-axis
                            style={{
                                grid: { stroke: dsColors.gray10, strokeWidth: 1 },
                                axis: { stroke: 'none' },
                                tickLabels: { fontFamily: defaultFont, fill: dsColors.gray55 },
                            }}
                            tickValues={metadata?.yAxisLabels}
                        />
                        <VictoryGroup
                            minDomain={{ y: metadata?.minY }} // min percentile score
                            maxDomain={{ y: metadata?.maxY }} // max percentile score
                            data={wellbeingReport}
                            color={dsColors.cyan30}
                            domainPadding={{ x: [0, 0], y: [40, 36] }}
                        >
                            <VictoryLine interpolation="monotoneX" />
                            <VictoryScatter
                                size={12}
                                symbol="circle"
                                style={{
                                    labels: { fill: dsColors.white, fontSize: 12 },
                                }}
                                labels={({ datum }) => datum.y}
                                labelComponent={<VictoryLabel dy={6} />}
                            />
                        </VictoryGroup>
                        <VictoryAxis
                            crossAxis // x-axis
                            style={{
                                axis: { stroke: 'none' },
                                tickLabels: { fontFamily: defaultFont, fill: dsColors.gray55 },
                            }}
                            tickFormat={(first14Days, i) => (i % 2 === 0 ? first14Days : '')}
                            tickLabelComponent={<VictoryLabel dy={10} />}
                            tickValues={metadata?.xAxisLabels}
                        />
                    </VictoryChart>
                </ChartWrapper>
            )}
            <WellbeingReportSummaryModal visible={showModal} onCancel={() => setShowModal(false)} />
        </Wrapper>
    );
};
